import { GlobalStyle, NotificationsProvider, UseState } from '@on-arte/ui';
import React, { useEffect, useMemo, useState } from 'react';
import TagManager from 'react-gtm-module';
import { QueryClient, QueryClientProvider } from 'react-query';
import { Navigate, Route, Routes } from 'react-router-dom';
import io, { Socket } from 'socket.io-client';

import { appConfig } from '@onArte/frontend/app.config';
import { AppWrapper, CheckAuth, Loader } from '@onArte/frontend/components';
import { SocketContext, TokenContext } from '@onArte/frontend/contexts';
import { RoutingState } from '@onArte/frontend/interfaces';
import {
  AboutUsView,
  SetPasswordView,
  HomeView,
  NotFoundView,
  RegistrationView,
  RemindPasswordView,
  SignInView,
  RegistrationConfirmView,
  ArtistDetailsView,
  ArtistsListView,
  ArtworkDetailsView,
  ArtworksListView,
  UserAccountSettingsView,
  UserSalesListView,
  UserArtworkAddView,
  UserArtworkDetailsView,
  UserArtworkSalesSettingsView,
  LogoutView,
  UserBiddingsListView,
  UserDeliverySettingsView,
  UserFavoriteArtworksView,
  UserMyShoppingView,
  UserPublicProfileSettingsView,
  CollectionsListView,
  CartBillingView,
  CartDeliveryView,
  HandbookBuyerView,
  HandbookSellerView,
  ContactView,
  CartSuccessView,
  FaqView,
  PrivacyPolicyView,
  CartPaymentErrorView,
  CartMissingItemView,
  UserDataForPayoutsView,
  UserSoldArtworksView,
  RegulationsView,
  UserArtworkEditView,
  CollectionView,
  CollectorDetailsView,
  CollectorsListView,
  PriceProposalAcceptedView,
  PriceProposalRejectedView,
  SearchView,
  ArtistsView
} from '@onArte/frontend/views';
import { RouteNameEnum } from '@onArte/shared/enums';
import { getArtworkCategorySegments, getRouteDetailsByName } from '@onArte/shared/utils';

export interface AppProps {
  /**
   * Klient zapytań przekazany z zewnątrz.
   *
   * Serwer podaje tu klienta wypełnionego danymi (ssr.helper.tsx), a przeglądarka —
   * klienta odtworzonego ze stanu osadzonego w HTML (index.tsx). Dzięki temu pierwsze
   * renderowanie po obu stronach opiera się na tych samych danych i React nie zgłasza
   * rozjazdu przy podłączaniu.
   *
   * Gdy prop nie zostanie podany, komponent tworzy własnego klienta — czyli zachowuje
   * się dokładnie jak dotąd.
   */
  queryClient?: QueryClient;

  /**
   * Stan routingu wyliczony przy renderowaniu serwerowym.
   *
   * Dotyczy stron o ładnych adresach (/dziela/nazwa-dziela), gdzie identyfikator
   * encji ustalany jest normalnie w useEffect — a więc nigdy na serwerze.
   * Przekazany tutaj, staje się stanem początkowym i pierwsze renderowanie
   * po obu stronach jest identyczne.
   */
  initialRoutingState?: RoutingState;
}

const App: React.FC<AppProps> = (
  { queryClient: injectedQueryClient, initialRoutingState }: AppProps
): JSX.Element => {
  const [isLoading, setIsLoading]: UseState<boolean> = useState<boolean>(true);
  const [socket, setSocket]: UseState<Socket | undefined> = useState<Socket | undefined>();
  const [gtmIdInitialized, setGtmIdInitialized]: UseState<boolean> = useState<boolean>(false);
  const queryClient: QueryClient = useMemo(
    (): QueryClient => injectedQueryClient ?? new QueryClient({
      defaultOptions: { queries: { refetchOnWindowFocus: false, retry: false } },
    }),
    [injectedQueryClient]
  );

  useEffect(
    (): (() => void) => {
      const socketIo: Socket = io(appConfig.websocket, { path: '/ws' });
      socketIo.on('connect', (): void => setSocket(socketIo));
      socketIo.on('disconnect', (): void => setSocket(undefined));

      return (): void => {
        socketIo.off('connect');
        socketIo.off('disconnect');
        socketIo.close();
      };
    },
    []
  );

  useEffect(
    (): void => {
      if (appConfig.gtmId && !gtmIdInitialized) {
        TagManager.initialize({ gtmId: appConfig.gtmId });
        setGtmIdInitialized(true);
      }
    },
    []
  );

  useEffect(
    (): void => {
      // TODO: change setTimeout to event listener
      setTimeout((): void => setIsLoading(false), 1500);
    },
    []
  );

  return (
    <SocketContext.Provider value={socket}>
      <Loader isVisible={isLoading} />
      <TokenContext.Consumer>
        {(tokenIncjectedBySSR: string): JSX.Element => (
          <QueryClientProvider client={queryClient}>
            <NotificationsProvider>
              <AppWrapper token={tokenIncjectedBySSR} initialRoutingState={initialRoutingState}>
                <GlobalStyle />
                <Routes>
                  <Route path={getRouteDetailsByName(RouteNameEnum.AboutUs)?.url} element={
                    <CheckAuth><AboutUsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.ArtistDetails)?.url} element={
                    <CheckAuth><ArtistDetailsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Artists)?.url} element={
                    <CheckAuth><ArtistsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.ArtistsList)?.url} element={
                    <CheckAuth><ArtistsListView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.ArtworkDetails)?.url} element={
                    <CheckAuth><ArtworkDetailsView /></CheckAuth>
                  } />
                  {/*
                    Cztery konkretne trasy zamiast wzorca /dziela/:category/:id — inaczej
                    zmyślony segment (np. /dziela/cokolwiek/xyz) dopasowałby się tutaj
                    i pokazał pustą stronę dzieła zamiast strony „nie znaleziono".
                  */}
                  {getArtworkCategorySegments().map((segment: string): JSX.Element => (
                    <Route key={segment} path={`/dziela/${segment}/:id`} element={
                      <CheckAuth><ArtworkDetailsView /></CheckAuth>
                    } />
                  ))}
                  <Route path={getRouteDetailsByName(RouteNameEnum.ArtworksList)?.url} element={
                    <CheckAuth><ArtworksListView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.CartBilling)?.url} element={
                    <CheckAuth><CartBillingView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.CartDelivery)?.url} element={
                    <CheckAuth><CartDeliveryView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.CartMissingItem)?.url} element={
                    <CheckAuth><CartMissingItemView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.CartPaymentError)?.url} element={
                    <CheckAuth><CartPaymentErrorView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.CartSuccess)?.url} element={
                    <CheckAuth><CartSuccessView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.ChoosePassword)?.url} element={
                    <CheckAuth><SetPasswordView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Collection)?.url} element={
                    <CheckAuth><CollectionView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.CollectionsList)?.url} element={
                    <CheckAuth><CollectionsListView /></CheckAuth>
                  } />
                  {!appConfig.hiddenModules.collectors && (
                    <Route path={getRouteDetailsByName(RouteNameEnum.CollectorDetails)?.url} element={
                      <CheckAuth><CollectorDetailsView /></CheckAuth>
                    } />
                  )}
                  {!appConfig.hiddenModules.collectors && (
                    <Route path={getRouteDetailsByName(RouteNameEnum.CollectorsList)?.url} element={
                      <CheckAuth><CollectorsListView /></CheckAuth>
                    } />
                  )}
                  <Route path={getRouteDetailsByName(RouteNameEnum.Contact)?.url} element={
                    <CheckAuth><ContactView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.EventDetails)?.url} element={
                    <Navigate to={getRouteDetailsByName(RouteNameEnum.Home)?.url ?? '/'} replace />
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.EventsList)?.url} element={
                    <Navigate to={getRouteDetailsByName(RouteNameEnum.Home)?.url ?? '/'} replace />
                  } />
                  <Route path='/magazyn' element={<Navigate to={getRouteDetailsByName(RouteNameEnum.Home)?.url ?? '/'} replace />} />
                  <Route path='/magazyn/*' element={<Navigate to={getRouteDetailsByName(RouteNameEnum.Home)?.url ?? '/'} replace />} />
                  <Route path={getRouteDetailsByName(RouteNameEnum.HandbookBuyer)?.url} element={
                    <CheckAuth><HandbookBuyerView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.HandbookSeller)?.url} element={
                    <CheckAuth><HandbookSellerView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Faq)?.url} element={
                    <CheckAuth><FaqView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Home)?.url} element={
                    <CheckAuth><HomeView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Logout)?.url} element={
                    <CheckAuth><LogoutView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.PriceProposalAccepted)?.url} element={
                    <CheckAuth><PriceProposalAcceptedView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.PriceProposalRejected)?.url} element={
                    <CheckAuth><PriceProposalRejectedView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.PrivacyPolicy)?.url} element={
                    <CheckAuth><PrivacyPolicyView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Registration)?.url} element={
                    <CheckAuth><RegistrationView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.RegistrationConfirm)?.url} element={
                    <CheckAuth><RegistrationConfirmView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Regulations)?.url} element={
                    <CheckAuth><RegulationsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.RemindPassword)?.url} element={
                    <CheckAuth><RemindPasswordView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserAccountSettings)?.url} element={
                    <CheckAuth><UserAccountSettingsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserArtworkAdd)?.url} element={
                    <CheckAuth><UserArtworkAddView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserArtworkDetails)?.url} element={
                    <CheckAuth><UserArtworkDetailsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserArtworkEdit)?.url} element={
                    <CheckAuth><UserArtworkEditView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserArtworkSalesSettings)?.url} element={
                    <CheckAuth><UserArtworkSalesSettingsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserBiddingsList)?.url} element={
                    <CheckAuth><UserBiddingsListView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserDataForPayouts)?.url} element={
                    <CheckAuth><UserDataForPayoutsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserDeliverySettings)?.url} element={
                    <CheckAuth><UserDeliverySettingsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserFavoriteArtworks)?.url} element={
                    <CheckAuth><UserFavoriteArtworksView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserMyShopping)?.url} element={
                    <CheckAuth><UserMyShoppingView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserPublicProfileSettings)?.url} element={
                    <CheckAuth><UserPublicProfileSettingsView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserSalesList)?.url} element={
                    <CheckAuth><UserSalesListView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.UserSoldArtworks)?.url} element={
                    <CheckAuth><UserSoldArtworksView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.Search)?.url} element={
                    <CheckAuth><SearchView /></CheckAuth>
                  } />
                  <Route path={getRouteDetailsByName(RouteNameEnum.SignIn)?.url} element={
                    <CheckAuth><SignInView /></CheckAuth>
                  } />
                  <Route path='*' element={<NotFoundView />} />
                </Routes>
              </AppWrapper>
            </NotificationsProvider>
          </QueryClientProvider>
        )}
      </TokenContext.Consumer>
    </SocketContext.Provider>
  );
};

export default App;
